import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import {
  createWhitelistEmail,
  deleteWhitelistEmail,
  fetchWhitelistEmails,
  updateWhitelistEmail,
} from '../../../api/adminWhitelist';
import type { WhitelistEmail } from '../../../api/adminWhitelist';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { SearchInput } from '../../../components/ui/search-input';
import { Spinner } from '../../../components/ui/spinner';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useNotification } from '../../../context/NotificationProvider';
import { classifyError, notifyMutationError } from '../../../lib/errors';

const PAGE_SIZE = 20;

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export function WhitelistEmailsTab() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', motivo: '' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'whitelist-emails', { q: search, page }],
    queryFn: () => fetchWhitelistEmails({ q: search || undefined, page, page_size: PAGE_SIZE }),
    placeholderData: (previous) => previous,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createWhitelistEmail({ email: form.email.trim(), motivo: form.motivo.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist-emails'] });
      showSuccess('Email adicionado à whitelist.');
      setDialogOpen(false);
      setForm({ email: '', motivo: '' });
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) =>
      updateWhitelistEmail(id, { ativo }),
    onMutate: async ({ id, ativo }) => {
      await queryClient.cancelQueries({ queryKey: ['admin', 'whitelist-emails'] });
      queryClient.setQueriesData(
        { queryKey: ['admin', 'whitelist-emails'] },
        (prev: { items: WhitelistEmail[]; total: number; page: number; page_size: number } | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.map((item) => (item.id === id ? { ...item, ativo } : item)),
          };
        },
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist-emails'] });
      showSuccess(variables.ativo ? 'Email ativado na whitelist.' : 'Email desativado na whitelist.');
    },
    onError: (err) => {
      notifyMutationError(err, showError);
      queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist-emails'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWhitelistEmail(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'whitelist-emails'] });
      showSuccess('Email removido da whitelist.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          value={search}
          onChange={(value) => {
            setPage(1);
            setSearch(value);
          }}
          placeholder="Buscar por email..."
          className="flex-1 max-w-sm"
        />
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Adicionar email
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading && !data ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size={32} label="Carregando whitelist" />
            </div>
          ) : data && data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--color-text-secondary)]">
              Nenhum email cadastrado ainda.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-24 text-center">Ativo</TableHead>
                  <TableHead className="hidden md:table-cell">Motivo</TableHead>
                  <TableHead className="hidden sm:table-cell min-w-[10rem]">Cadastrado</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium break-all">
                      <div className="flex flex-col gap-0.5">
                        <span>{item.email}</span>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                          <span>{formatDateTime(item.created_at)}</span>
                          {item.motivo && <span>{item.motivo}</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={item.ativo}
                        onCheckedChange={(checked) =>
                          toggleMutation.mutate({ id: item.id, ativo: Boolean(checked) })
                        }
                        aria-label={`Alternar ${item.email}`}
                      />
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-[var(--color-text-secondary)]">
                      {item.motivo ?? '—'}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-[var(--color-text-tertiary)]">
                      {formatDateTime(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => deleteMutation.mutate(item.id)}
                        disabled={deleteMutation.isPending}
                        aria-label={`Remover ${item.email}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-[var(--color-text-secondary)]">
          <span>
            Página {data.page} de {totalPages} ({data.total} emails)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar email à whitelist</DialogTitle>
            <DialogDescription>
              O email passa a ser aceito no onboarding. No primeiro login recebe cortesia premium automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="whitelist-email">Email</Label>
              <Input
                id="whitelist-email"
                type="email"
                autoFocus
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                placeholder="advogado@exemplo.com"
              />
            </div>
            <div>
              <Label htmlFor="whitelist-motivo">Motivo (opcional)</Label>
              <Input
                id="whitelist-motivo"
                value={form.motivo}
                onChange={(event) => setForm({ ...form, motivo: event.target.value })}
                placeholder="Ex: Piloto jun/26 — indicação do Dr. Fulano"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!form.email.trim() || createMutation.isPending}
            >
              {createMutation.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
